import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import apiService from '../services/api-service';
import { useAuth } from '../contexts/auth-context';
import { useToast } from '../contexts/toast-context';
import { useLanguage } from '../contexts/language-context';
import AuthModal from './auth-modal';
import EmailConfirmationModal from './email-confirmation-modal';
import ForgotPasswordModal from './forgot-password-modal';
import ResetPasswordModal from './reset-password-modal';
import VerifyEmailModal from './verify-email-modal';
import { buildImageUrl, RouteHelpers, AUTH_MESSAGES, CONSOLE_MESSAGES, TIMEOUTS, FALLBACK_IMAGES } from '../constants';
import { getRank } from '../utils/ranks';


function Layout({ children, searchQuery, setSearchQuery, resetFilters }) {
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHadEmptyResponse, setSearchHadEmptyResponse] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [dismissedEmailNotice, setDismissedEmailNotice] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const userMenuRef = useRef(null);

  const { user, isAuthenticated, logout, resendEmailVerification } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language, setLanguage, t, languages } = useLanguage();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetPasswordToken = urlParams.get('reset-password');
    const verifyEmailToken = location.pathname === '/verify-email' ? urlParams.get('token') : null;

    if (resetPasswordToken) {
      setResetToken(resetPasswordToken);
      setShowResetPassword(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (verifyEmailToken) {
      setVerifyToken(verifyEmailToken);
      setShowVerifyEmail(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, location.pathname]);

  useEffect(() => {
    const savedCooldownEnd = localStorage.getItem('emailResendCooldownEnd');
    if (savedCooldownEnd) {
      const remaining = Math.max(0, Math.floor((parseInt(savedCooldownEnd) - Date.now()) / TIMEOUTS.COOLDOWN_TIMER));
      setResendCooldown(remaining);
    }
  }, []);

  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            localStorage.removeItem('emailResendCooldownEnd');
            return 0;
          }
          return newValue;
        });
      }, TIMEOUTS.COOLDOWN_TIMER);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const isMoviesPage = location.pathname === '/' || location.pathname.startsWith('/movie');
  const isTVPage = location.pathname.startsWith('/tv');
  const isSearchPage = location.pathname.startsWith('/search');
  const isCinemaPage = location.pathname === '/cinema';
  const isWatchedPage = location.pathname === '/watched';
  const isWatchlistPage = location.pathname === '/watchlist';
  const isFavoritesPage = location.pathname === '/favorites';
  const isCollectionsPage = location.pathname === '/collections';
  const isContactPage = location.pathname === '/kontaktai';

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (globalSearchQuery.trim().length >= 2) {
        fetchSearchSuggestions(globalSearchQuery.trim());
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
        setSearchHadEmptyResponse(false);
      }
    }, TIMEOUTS.DEBOUNCE_SEARCH);

    return () => clearTimeout(timeoutId);
  }, [globalSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleOpenAuthModal = (event) => {
      const { mode } = event.detail;
      setAuthModalMode(mode);
      setShowAuthModal(true);
    };

    window.addEventListener('openAuthModal', handleOpenAuthModal);
    return () => window.removeEventListener('openAuthModal', handleOpenAuthModal);
  }, []);

  // Scroll to top on route changes from mobile navigation
  useEffect(() => {
    const handleScrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    };

    window.addEventListener('mobileNavigation', handleScrollToTop);
    return () => window.removeEventListener('mobileNavigation', handleScrollToTop);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 12);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fetchSearchSuggestions = async (query) => {
    setIsSearching(true);
    setSearchHadEmptyResponse(false);

    try {
      const data = await apiService.getSearchSuggestions(query);
      const results = data.results || [];
      setSearchSuggestions(results);
      const isEmpty = results.length === 0;
      setSearchHadEmptyResponse(isEmpty);
      setShowSuggestions(query.trim().length >= 2);
    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_FETCHING_SEARCH, error);
      setSearchSuggestions([]);
      setSearchHadEmptyResponse(false);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleGlobalSearch = (e) => {
    e.preventDefault();
    if (globalSearchQuery.trim()) {
      setShowSuggestions(false);
      navigate(RouteHelpers.search(globalSearchQuery.trim()));
    }
  };

  const handleSuggestionClick = (item) => {
    setShowSuggestions(false);
    setGlobalSearchQuery('');
    
    if (item.media_type === 'movie') {
      navigate(RouteHelpers.movieDetail(item.id));
    } else if (item.media_type === 'tv') {
      navigate(RouteHelpers.tvDetail(item.id));
    }
  };

  const handleInputFocus = () => {
    const q = globalSearchQuery.trim();
    if (q.length >= 2 && (searchSuggestions.length > 0 || searchHadEmptyResponse)) {
      setShowSuggestions(true);
    }
  };

  const getImageUrl = (posterPath) => {
    return posterPath 
      ? buildImageUrl(posterPath, 'w92')
      : '/placeholder-poster.jpg';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).getFullYear();
  };

  const handleAuthClick = (mode) => {
    setAuthModalMode(mode);
    setShowAuthModal(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error(CONSOLE_MESSAGES.LOGOUT_ERROR, error);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    
    setIsResendingEmail(true);
    try {
      await resendEmailVerification();
      
      const cooldownDuration = TIMEOUTS.COOLDOWN_DEFAULT;
      const cooldownEnd = Date.now() + (cooldownDuration * TIMEOUTS.COOLDOWN_TIMER);
      localStorage.setItem('emailResendCooldownEnd', cooldownEnd.toString());
      setResendCooldown(cooldownDuration);
      
      showSuccess(AUTH_MESSAGES.VERIFICATION_EMAIL_SENT);
    } catch (error) {
      console.error(CONSOLE_MESSAGES.FAILED_RESEND_VERIFICATION, error);
      
      if (error.message && error.message.includes('Per daug el. laiškų')) {
        const cooldownDuration = TIMEOUTS.COOLDOWN_RATE_LIMIT;
        const cooldownEnd = Date.now() + (cooldownDuration * TIMEOUTS.COOLDOWN_TIMER);
        localStorage.setItem('emailResendCooldownEnd', cooldownEnd.toString());
        setResendCooldown(cooldownDuration);
        showError(error.message);
      } else {
        showError(AUTH_MESSAGES.VERIFICATION_EMAIL_ERROR);
      }
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleDismissEmailNotice = () => {
    setDismissedEmailNotice(true);
  };

  const handleLogoClick = () => {
    // Clear any global search query
    setGlobalSearchQuery('');
    
    // Always reset filters when logo is clicked
    if (resetFilters) {
      resetFilters();
    }
    
    // Clear all filter storage and view mode to ensure clean state when navigating
    try {
      localStorage.removeItem('filmudb_shared_filters');
      localStorage.removeItem('filmudb_movie_filters');
      localStorage.removeItem('filmudb_tv_filters');
      localStorage.removeItem('filmudb_collection_filters');
      localStorage.removeItem('filmudb_view_mode');
    } catch (error) {
      console.error('Error clearing filter storage:', error);
    }
    
    // Check if we're already on the home page
    const isHomePage = location.pathname === '/';
    
    if (!isHomePage) {
      // If on any other page, navigate to home with clean state
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 pb-16 pt-[72px] lg:pb-0">
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color] duration-300 ${
          headerScrolled
            ? 'bg-dark-900/55 border-dark-700/35'
            : 'bg-dark-900/95 border-dark-700'
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex h-[72px] min-w-0 flex-nowrap items-center gap-x-3 lg:gap-6">
            <div className="order-1 flex min-w-0 shrink-0 items-center gap-3 lg:gap-5 lg:min-h-0 lg:overflow-x-auto lg:overscroll-x-contain lg:[scrollbar-width:thin]">
              <button 
                type="button"
                onClick={handleLogoClick}
                className="flex min-w-0 items-center gap-2.5 hover:opacity-80 transition-opacity group shrink-0 p-0 m-0 border-0 bg-transparent"
                title="Grįžti į pradžią ir išvalyti filtrus"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14 16a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2h8z" />
                  </svg>
                </div>
                <div className="hidden sm:block">
                  <div className="text-xl font-bold text-white group-hover:text-primary-300 transition-colors leading-tight">FilmųDB</div>
                  <p className="text-xs text-gray-400 leading-tight">Filmai ir TV serialai</p>
                </div>
              </button>

              <nav
                className="hidden lg:flex lg:flex-none lg:flex-row lg:flex-nowrap lg:items-center lg:gap-x-3 xl:gap-x-5 2xl:gap-x-6 lg:min-w-max lg:py-0.5 [&_a]:shrink-0 [&_button]:shrink-0 [&_span]:shrink-0 [&_a]:whitespace-nowrap [&_button]:whitespace-nowrap [&_span]:whitespace-nowrap"
                aria-label="Pagrindinė navigacija"
              >
                <button 
                  onClick={() => navigate('/')}
                  className={`font-medium transition-colors shrink-0 whitespace-nowrap ${
                    isMoviesPage ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {t('movies')}
                </button>
                <button 
                  onClick={() => navigate('/tv')}
                  className={`font-medium transition-colors shrink-0 whitespace-nowrap ${
                    isTVPage ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {t('tvShows')}
                </button>
                <button 
                  onClick={() => navigate('/cinema')}
                  className={`font-medium transition-colors shrink-0 whitespace-nowrap ${
                    isCinemaPage ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {t('cinema')}
                </button>
                {isSearchPage && (
                  <span className="text-primary-400 font-medium shrink-0 whitespace-nowrap">
                    Paieškos rezultatai
                  </span>
                )}
                {isAuthenticated && (
                  <>
                    <button 
                      onClick={() => navigate('/watchlist')}
                      className={`font-medium transition-colors shrink-0 whitespace-nowrap ${
                        location.pathname === '/watchlist' ? 'text-white' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {t('watchlist')}
                    </button>
                    <button 
                      onClick={() => navigate('/watched')}
                      className={`font-medium transition-colors shrink-0 whitespace-nowrap ${
                        isWatchedPage ? 'text-white' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {t('watched')}
                    </button>
                  </>
                )}
                <button 
                  onClick={() => navigate('/collections')}
                  className={`font-medium transition-colors shrink-0 whitespace-nowrap ${
                    isCollectionsPage ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {t('collections')}
                </button>
                <Link
                  to="/kontaktai"
                  className={`font-medium transition-colors shrink-0 whitespace-nowrap ${
                    isContactPage ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {t('contactNav')}
                </Link>
              </nav>
            </div>

            <div className="order-3 flex shrink-0 items-center gap-1.5 sm:gap-2">
              {isAuthenticated ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-1 h-10 py-0 pl-1 pr-1 sm:px-1.5 rounded-lg hover:bg-dark-700/50 transition-colors"
                    aria-expanded={showUserMenu}
                    aria-haspopup="true"
                  >
                    {(() => {
                      const rank = getRank(user?.points || 0);
                      return (
                        <div className="flex flex-row items-center gap-0 2xl:flex-col 2xl:gap-0.5 2xl:items-center">
                          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-semibold text-sm">
                              {user?.initials || user?.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <span
                            className="text-[9px] font-semibold leading-none whitespace-nowrap hidden 2xl:block"
                            style={{ color: rank.color }}
                          >
                            {t(rank.key)}
                          </span>
                        </div>
                      );
                    })()}
                    <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-dark-800/95 backdrop-blur-xl border border-dark-700 rounded-lg shadow-2xl z-50">
                      <div className="p-4 border-b border-dark-700">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold">
                              {user?.initials || user?.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{user?.name}</p>
                            <p className="text-gray-400 text-sm truncate">{user?.email}</p>
                            {(() => {
                              const rank = getRank(user?.points || 0);
                              return (
                                <span
                                  className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-1 leading-none"
                                  style={{ backgroundColor: `${rank.color}20`, color: rank.color }}
                                >
                                  {rank.icon} {t(rank.key)} · {user?.points || 0} {t('pointsShort')}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="py-2">
                        <button 
                          onClick={() => {
                            navigate('/favorites');
                            setShowUserMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-gray-300 hover:text-white hover:bg-dark-700/50 transition-colors flex items-center space-x-3"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                          <span>{t('favorites')}</span>
                        </button>
                        <button 
                          onClick={() => {
                            navigate('/watchlist');
                            setShowUserMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-gray-300 hover:text-white hover:bg-dark-700/50 transition-colors flex items-center space-x-3"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span>{t('watchlist')}</span>
                        </button>
                        <button 
                          onClick={() => {
                            navigate('/watched');
                            setShowUserMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-gray-300 hover:text-white hover:bg-dark-700/50 transition-colors flex items-center space-x-3"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>{t('watched')}</span>
                        </button>
                        <div className="border-t border-dark-700 mt-2 pt-2">
                          <div className="px-4 py-2">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">{t('selectLanguage')}</p>
                            <div className="flex items-center gap-1.5">
                              {Object.values(languages).map((lang) => (
                                <button
                                  key={lang.code}
                                  onClick={() => {
                                    setLanguage(lang.code);
                                  }}
                                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                    language === lang.code
                                      ? 'bg-primary-600/20 text-primary-300 ring-1 ring-primary-500/30'
                                      : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                                  }`}
                                >
                                  <span className="text-base leading-none">{lang.flag}</span>
                                  <span>{lang.code.toUpperCase()}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-dark-700 pt-2 pb-1">
                          <button 
                            onClick={handleLogout}
                            className="w-full px-4 py-2 text-left text-red-400 hover:text-red-300 hover:bg-dark-700/50 transition-colors flex items-center space-x-3"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span>{t('logout')}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1 sm:gap-2">
                  <button
                    onClick={() => handleAuthClick('login')}
                    className="flex items-center justify-center h-9 sm:h-10 px-2 sm:px-4 text-gray-300 hover:text-white rounded-lg transition-colors text-xs sm:text-sm font-medium sm:border sm:border-dark-600/50 sm:hover:bg-dark-700/50 whitespace-nowrap"
                  >
                    {t('login')}
                  </button>
                  <button
                    onClick={() => handleAuthClick('register')}
                    className="flex items-center justify-center h-9 sm:h-10 px-2.5 sm:px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-all text-xs sm:text-sm whitespace-nowrap"
                  >
                    {t('register')}
                  </button>
                </div>
              )}
            </div>

            <div className="order-2 min-w-0 flex-1 self-center">
              <div
                className="relative flex min-h-0 w-full min-w-0 items-center"
                ref={searchRef}
              >
              <form onSubmit={handleGlobalSearch} className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={globalSearchQuery}
                  onChange={(e) => setGlobalSearchQuery(e.target.value)}
                  onFocus={handleInputFocus}
                  className="w-full h-10 pl-10 pr-10 bg-dark-800 border border-dark-600 text-gray-200 placeholder-gray-500 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
                <button
                  type="submit"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary-400 transition-colors"
                >
                  {isSearching ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent"></div>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  )}
                </button>
              </form>

              {showSuggestions && (
                <div 
                  ref={suggestionsRef}
                  className="fixed left-4 right-4 top-[72px] z-50 max-h-96 overflow-y-auto custom-scrollbar bg-dark-800/95 backdrop-blur-xl border border-dark-700 rounded-xl shadow-2xl max-lg:max-h-[min(24rem,calc(100vh-8rem))] lg:absolute lg:left-0 lg:right-0 lg:top-full lg:mt-2 lg:w-full"
                >
                  {isSearching && searchSuggestions.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-400 text-sm">
                      {t('searchDropdownLoading')}
                    </div>
                  ) : searchHadEmptyResponse && searchSuggestions.length === 0 ? (
                    <>
                      <div className="px-4 py-6 text-center">
                        <p className="text-white font-medium">{t('searchSuggestionsEmpty')}</p>
                        <p className="text-gray-400 text-sm mt-2">{t('searchSuggestionsEmptyHint')}</p>
                      </div>
                      {globalSearchQuery.trim() && (
                        <button
                          type="button"
                          onClick={handleGlobalSearch}
                          className="w-full px-4 py-3 text-center text-primary-400 hover:text-primary-300 hover:bg-dark-700/30 transition-colors font-medium border-t border-dark-700/50"
                        >
                          {t('showAllResults')} &quot;{globalSearchQuery}&quot;
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      {searchSuggestions.map((item) => (
                        <button
                          key={`${item.media_type}-${item.id}`}
                          type="button"
                          onClick={() => handleSuggestionClick(item)}
                          className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-dark-700/50 transition-colors text-left border-b border-dark-700/30 last:border-b-0"
                        >
                          <div className="flex-shrink-0 w-12 h-16 bg-dark-700 rounded overflow-hidden">
                            <img
                              src={getImageUrl(item.poster_path)}
                              alt={item.title || item.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = FALLBACK_IMAGES.POSTER_SMALL;
                              }}
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium truncate">
                              {item.title || item.name}
                            </h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <p className="text-gray-400 text-sm">
                                {formatDate(item.release_date || item.first_air_date)}
                              </p>
                              {item.media_type === 'movie' ? (
                                <span className="bg-primary-600 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                                  Filmas
                                </span>
                              ) : (
                                <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                                  TV
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex-shrink-0 text-gray-500">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </button>
                      ))}

                      {globalSearchQuery.trim() && (
                        <button
                          type="button"
                          onClick={handleGlobalSearch}
                          className="w-full px-4 py-3 text-center text-primary-400 hover:text-primary-300 hover:bg-dark-700/30 transition-colors font-medium border-t border-dark-700/50"
                        >
                          {t('showAllResults')} &quot;{globalSearchQuery}&quot;
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {isAuthenticated && user && !user.isEmailVerified && !dismissedEmailNotice && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-yellow-200 font-medium">
                    Patvirtinkite savo el. pašto adresą
                  </p>
                  <p className="text-xs text-yellow-300/80">
                    Patikrinkite savo el. paštą ir spauskite patvirtinimo nuorodą, kad galėtumėte naudotis visomis funkcijomis.
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleResendVerification}
                  disabled={isResendingEmail || resendCooldown > 0}
                  className="text-yellow-300 hover:text-yellow-200 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  {isResendingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border border-yellow-300 border-t-transparent"></div>
                      <span>Siunčiama...</span>
                    </>
                  ) : resendCooldown > 0 ? (
                    <div className="flex items-center space-x-1 relative">
                      <div className="relative">
                        <svg className="w-3 h-3 transform -rotate-90" viewBox="0 0 20 20">
                          <circle
                            cx="10"
                            cy="10"
                            r="8"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeOpacity="0.3"
                          />
                          <circle
                            cx="10"
                            cy="10"
                            r="8"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeDasharray={`${2 * Math.PI * 8}`}
                            strokeDashoffset={`${2 * Math.PI * 8 * (resendCooldown / 60)}`}
                            strokeLinecap="round"
                            className="transition-all duration-1000 ease-linear"
                          />
                        </svg>
                      </div>
                      <span>{Math.floor(resendCooldown / 60)}:{(resendCooldown % 60).toString().padStart(2, '0')}</span>
                    </div>
                  ) : (
                    'Siųsti iš naujo'
                  )}
                </button>
                <button 
                  onClick={handleDismissEmailNotice}
                  className="text-yellow-300 hover:text-yellow-200 transition-colors p-1"
                  aria-label="Paslėpti pranešimą"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {children}

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-dark-900/95 backdrop-blur-xl border-t border-dark-700 safe-area-inset-bottom">
        <div className={`grid ${isAuthenticated ? 'grid-cols-5' : 'grid-cols-4'} h-16`}>
          {/* Movies */}
          <button
            onClick={() => {
              window.dispatchEvent(new Event('mobileNavigation'));
              navigate('/');
            }}
            className={`flex flex-col items-center justify-center space-y-0.5 transition-colors ${
              isMoviesPage ? 'text-primary-400' : 'text-gray-400 active:text-gray-300'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
            </svg>
            <span className="text-[10px] font-medium leading-tight">{t('moviesShort')}</span>
          </button>

          {/* TV Shows */}
          <button
            onClick={() => {
              window.dispatchEvent(new Event('mobileNavigation'));
              navigate('/tv');
            }}
            className={`flex flex-col items-center justify-center space-y-0.5 transition-colors ${
              isTVPage ? 'text-primary-400' : 'text-gray-400 active:text-gray-300'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-[10px] font-medium leading-tight">{t('tvShort')}</span>
          </button>

          {isAuthenticated ? (
            <>
              {/* Favorites */}
              <button
                onClick={() => {
                  window.dispatchEvent(new Event('mobileNavigation'));
                  navigate('/favorites');
                }}
                className={`flex flex-col items-center justify-center space-y-0.5 transition-colors ${
                  isFavoritesPage ? 'text-primary-400' : 'text-gray-400 active:text-gray-300'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span className="text-[10px] font-medium leading-tight">{t('favoritesShort')}</span>
              </button>

              {/* Watched */}
              <button
                onClick={() => {
                  window.dispatchEvent(new Event('mobileNavigation'));
                  navigate('/watched');
                }}
                className={`flex flex-col items-center justify-center space-y-0.5 transition-colors ${
                  isWatchedPage ? 'text-primary-400' : 'text-gray-400 active:text-gray-300'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="text-[10px] font-medium leading-tight">{t('watchedShort')}</span>
              </button>

              {/* Watchlist */}
              <button
                onClick={() => {
                  window.dispatchEvent(new Event('mobileNavigation'));
                  navigate('/watchlist');
                }}
                className={`flex flex-col items-center justify-center space-y-0.5 transition-colors ${
                  isWatchlistPage ? 'text-primary-400' : 'text-gray-400 active:text-gray-300'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span className="text-[10px] font-medium leading-tight">{t('watchlistShort')}</span>
              </button>
            </>
          ) : (
            <>
              {/* Cinema */}
              <button
                onClick={() => {
                  window.dispatchEvent(new Event('mobileNavigation'));
                  navigate('/cinema');
                }}
                className={`flex flex-col items-center justify-center space-y-0.5 transition-colors ${
                  isCinemaPage ? 'text-primary-400' : 'text-gray-400 active:text-gray-300'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
                <span className="text-[10px] font-medium leading-tight">{t('cinemaShort')}</span>
              </button>

              {/* Collections */}
              <button
                onClick={() => {
                  window.dispatchEvent(new Event('mobileNavigation'));
                  navigate('/collections');
                }}
                className={`flex flex-col items-center justify-center space-y-0.5 transition-colors ${
                  isCollectionsPage ? 'text-primary-400' : 'text-gray-400 active:text-gray-300'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span className="text-[10px] font-medium leading-tight">{t('collectionsShort')}</span>
              </button>
            </>
          )}
        </div>
      </nav>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
        onRegistrationSuccess={(email) => {
          setRegisteredEmail(email);
          setShowAuthModal(false);
          setShowEmailConfirmation(true);
        }}
        onForgotPasswordClick={() => {
          setShowAuthModal(false);
          setShowForgotPassword(true);
        }}
      />

      <EmailConfirmationModal
        isOpen={showEmailConfirmation}
        onClose={() => setShowEmailConfirmation(false)}
        userEmail={registeredEmail}
      />

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />

      <ResetPasswordModal
        isOpen={showResetPassword}
        onClose={() => {
          setShowResetPassword(false);
          setResetToken('');
        }}
        token={resetToken}
      />

      <VerifyEmailModal
        isOpen={showVerifyEmail}
        onClose={() => {
          setShowVerifyEmail(false);
          setVerifyToken('');
        }}
        token={verifyToken}
      />
    </div>
  );
}

export default Layout;
